import { useEffect, useRef } from 'react';
import { toast } from '../../Components/Toast';
import { whenMapStyleReady } from '../../Map/hooks/useListPinsLayer';
import { CAD_PARCEL_STOCK_STYLE } from '../../Map/styles/mapTheme';
import { canAccessMapStyle } from '../../Map/utils/canAccessMapStyle';
import {
  attachCadCanvasImageElement,
  detachCadCanvasImageElement,
  isMapLayerAbove,
  prepareCadCanvasImageForMap,
  shouldReplaceCadCanvasImageSource,
} from '../utils/cadCanvasImage';
import { CAD_FILL_LAYER_ID, resolveCadOverlayInsertBeforeId } from './useCadParcelsLayer';
import { moveCadProjectPinsForeground } from '../utils/cadProjectPinLayers';

export const CAD_CANVAS_IMAGE_SOURCE_ID = 'cad-canvas-image';
export const CAD_CANVAS_IMAGE_LAYER_ID = 'cad-canvas-image';

function removeCadCanvasImage(map) {
  if (!map) return;
  try {
    if (map.getLayer(CAD_CANVAS_IMAGE_LAYER_ID)) map.removeLayer(CAD_CANVAS_IMAGE_LAYER_ID);
  } catch (_) { /* estilo a medias */ }
  try {
    if (map.getSource(CAD_CANVAS_IMAGE_SOURCE_ID)) map.removeSource(CAD_CANVAS_IMAGE_SOURCE_ID);
  } catch (_) { /* estilo a medias */ }
}

function coordinatesKey(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 4) return '';
  return coordinates.map((pair) => `${pair[0]},${pair[1]}`).join(';');
}

function cadCanvasImageBeforeId(map) {
  return resolveCadOverlayInsertBeforeId(map, CAD_FILL_LAYER_ID);
}

function moveCadCanvasImageBelowParcels(map) {
  const beforeId = cadCanvasImageBeforeId(map);
  const above = isMapLayerAbove(
    map.getStyle?.()?.layers,
    CAD_CANVAS_IMAGE_LAYER_ID,
    beforeId,
  );
  if (!above || !beforeId) return;
  try { map.moveLayer(CAD_CANVAS_IMAGE_LAYER_ID, beforeId); } catch (_) { /* estilo a medias */ }
  // Tras bajar la imagen, los pines (si hay) tienen que quedar encima del CAD.
  moveCadProjectPinsForeground(map);
}

/**
 * Mueve el quad sin recargar el bitmap. Es lo que tiene que usar el arrastre.
 * @param {import('maplibre-gl').Map|null} map
 * @param {number[][]|null} coordinates
 */
export function setCadCanvasImageCoordinates(map, coordinates) {
  if (!map || !Array.isArray(coordinates) || coordinates.length < 4) return false;
  try {
    const source = map.getSource(CAD_CANVAS_IMAGE_SOURCE_ID);
    if (!source || typeof source.setCoordinates !== 'function') return false;
    source.setCoordinates(coordinates);
    return true;
  } catch (_) {
    return false;
  }
}

function hasCadCanvasImageLayer(map) {
  try {
    return Boolean(map.getSource(CAD_CANVAS_IMAGE_SOURCE_ID) && map.getLayer(CAD_CANVAS_IMAGE_LAYER_ID));
  } catch (_) {
    return false;
  }
}

function readPreparedFrom(prepared) {
  if (!prepared) return '';
  if (typeof prepared === 'string') return prepared.trim();
  return String(prepared.from || prepared.url || '').trim();
}

function cadCanvasImageSourceSpec(prepared, coordinates) {
  if (prepared?.canvas) {
    return {
      type: 'canvas',
      canvas: prepared.canvas,
      coordinates,
      animate: false,
    };
  }
  const url = typeof prepared === 'string' ? prepared : prepared?.url;
  return {
    type: 'image',
    url,
    coordinates,
  };
}

function ensureCadCanvasImage(map, prepared, coordinates, lastFromRef, lastCanvasRef) {
  const nextFrom = readPreparedFrom(prepared);
  const existing = map.getSource(CAD_CANVAS_IMAGE_SOURCE_ID);
  const sameBitmap = existing && !shouldReplaceCadCanvasImageSource(lastFromRef.current, nextFrom);
  if (sameBitmap && typeof existing.setCoordinates === 'function') {
    try { existing.setCoordinates(coordinates); } catch (_) { /* quad a medias */ }
  } else {
    if (existing) removeCadCanvasImage(map);
    detachCadCanvasImageElement(lastCanvasRef.current);
    if (prepared?.canvas) attachCadCanvasImageElement(map, prepared.canvas);
    map.addSource(CAD_CANVAS_IMAGE_SOURCE_ID, cadCanvasImageSourceSpec(prepared, coordinates));
    lastFromRef.current = nextFrom;
    lastCanvasRef.current = prepared?.canvas || null;
  }
  if (map.getLayer(CAD_CANVAS_IMAGE_LAYER_ID)) {
    moveCadCanvasImageBelowParcels(map);
    return;
  }
  const layer = {
    id: CAD_CANVAS_IMAGE_LAYER_ID,
    type: 'raster',
    source: CAD_CANVAS_IMAGE_SOURCE_ID,
    paint: {
      'raster-opacity': 1,
      'raster-opacity-transition': { duration: 0, delay: 0 },
      'raster-fade-duration': 0,
    },
  };
  const beforeId = cadCanvasImageBeforeId(map);
  if (beforeId) map.addLayer(layer, beforeId);
  else map.addLayer(layer);
  moveCadProjectPinsForeground(map);
}

const FIRST_PAINT_RETRY_MS = [0, 120, 400, 1200];
const SOURCE_ERROR_RETRY_MS = 320;

function isCadCanvasImageReady(map) {
  if (!hasCadCanvasImageLayer(map)) return false;
  try {
    if (typeof map.isSourceLoaded === 'function') {
      return map.isSourceLoaded(CAD_CANVAS_IMAGE_SOURCE_ID);
    }
  } catch (_) { /* estilo a medias */ }
  return true;
}

/**
 * Raster debajo de los lotes. `coordinates` ya están en el CRS del mapa.
 * Un hueco breve de coordenadas no saca la capa: si no, al reemplazar
 * el PNG el preview queda y el mapa no.
 *
 * @param {{
 *   mapInstanceRef: { current: import('maplibre-gl').Map|null },
 *   enabled?: boolean,
 *   url?: string|null,
 *   coordinates?: number[][]|null,
 *   guideMode?: boolean,
 * }} options
 */
export function useCadCanvasImageLayer({
  mapInstanceRef,
  enabled = false,
  url = null,
  coordinates = null,
  guideMode = false,
}) {
  const coordsKey = coordinatesKey(coordinates);
  const lastFromRef = useRef(null);
  const lastCanvasRef = useRef(null);
  const notifiedUrlRef = useRef(null);
  const coordsRef = useRef(coordinates);
  if (coordinates) coordsRef.current = coordinates;
  const urlRef = useRef(url);
  urlRef.current = url;
  const resolvedRef = useRef({ from: '', prepared: null });
  const loadingUrlRef = useRef('');
  const applyRef = useRef(() => {});

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return undefined;
    if (!enabled || !url) {
      lastFromRef.current = null;
      coordsRef.current = null;
      loadingUrlRef.current = '';
      detachCadCanvasImageElement(lastCanvasRef.current);
      lastCanvasRef.current = null;
      resolvedRef.current = { from: '', prepared: null };
      removeCadCanvasImage(map);
      return undefined;
    }
    let cancelled = false;
    const replayTimers = [];
    let errorRetryTimer = 0;
    const clearReplayTimers = () => {
      while (replayTimers.length) window.clearTimeout(replayTimers.pop());
    };
    const clearDeferredTimers = () => {
      clearReplayTimers();
      if (errorRetryTimer) window.clearTimeout(errorRetryTimer);
      errorRetryTimer = 0;
    };

    const paint = (prepared) => {
      if (cancelled || !prepared) return false;
      const coords = coordsRef.current;
      if (!coords) return false;
      try {
        ensureCadCanvasImage(map, prepared, coords, lastFromRef, lastCanvasRef);
        return hasCadCanvasImageLayer(map);
      } catch (error) {
        console.error('CAD: no se pudo pintar la ilustración', error);
        return false;
      }
    };

    const armReplay = (prepared) => {
      clearReplayTimers();
      FIRST_PAINT_RETRY_MS.forEach((ms) => {
        replayTimers.push(window.setTimeout(() => {
          if (cancelled) return;
          if (resolvedRef.current.prepared !== prepared) return;
          if (hasCadCanvasImageLayer(map)) {
            moveCadCanvasImageBelowParcels(map);
            return;
          }
          paint(prepared);
        }, ms));
      });
    };

    const notifyDecodeFailed = (nextUrl) => {
      if (cancelled) return;
      if (notifiedUrlRef.current === nextUrl) return;
      notifiedUrlRef.current = nextUrl;
      console.error('CAD: no se pudo leer la imagen base', nextUrl);
      toast.error('No se pudo cargar la imagen base en el mapa.');
    };

    const retryBrokenSource = (force = false) => {
      if (cancelled) return;
      if (isCadCanvasImageReady(map)) return;
      const prepared = resolvedRef.current.prepared;
      if (!prepared) return;
      if (!force && hasCadCanvasImageLayer(map)) return;
      lastFromRef.current = null;
      try { removeCadCanvasImage(map); } catch (_) { /* estilo a medias */ }
      paint(prepared);
      armReplay(prepared);
    };

    const onError = (event) => {
      const sourceId = event?.sourceId || event?.source?.id;
      if (sourceId !== CAD_CANVAS_IMAGE_SOURCE_ID) return;
      if (errorRetryTimer) return;
      errorRetryTimer = window.setTimeout(() => {
        retryBrokenSource(false);
        errorRetryTimer = window.setTimeout(() => {
          errorRetryTimer = 0;
          retryBrokenSource(true);
        }, 900);
      }, SOURCE_ERROR_RETRY_MS);
    };
    map.on('error', onError);

    const apply = () => {
      if (cancelled) return;
      const nextUrl = urlRef.current;
      if (!nextUrl) return;
      if (resolvedRef.current.from === nextUrl && resolvedRef.current.prepared) {
        paint(resolvedRef.current.prepared);
        armReplay(resolvedRef.current.prepared);
        return;
      }
      if (loadingUrlRef.current === nextUrl) return;
      loadingUrlRef.current = nextUrl;
      void prepareCadCanvasImageForMap(nextUrl).then((prepared) => {
        if (loadingUrlRef.current === nextUrl) loadingUrlRef.current = '';
        if (cancelled || urlRef.current !== nextUrl) return;
        if (!prepared) {
          notifyDecodeFailed(nextUrl);
          return;
        }
        resolvedRef.current = { from: nextUrl, prepared };
        paint(prepared);
        armReplay(prepared);
      }).catch((error) => {
        if (loadingUrlRef.current === nextUrl) loadingUrlRef.current = '';
        if (cancelled) return;
        console.error('CAD: no se pudo leer la imagen base', error);
        notifyDecodeFailed(nextUrl);
      });
    };
    applyRef.current = apply;

    let unbindReady = () => {};
    if (map.isStyleLoaded()) apply();
    else unbindReady = whenMapStyleReady(map, apply);

    // Un cambio de estilo (vista de mapa, teselas, apagar al alinear) se lleva
    // las capas. El loteo se repinta solo; sin esto el raster no vuelve.
    // Si el source ya está, no lo recreamos: cancelaría la carga de la foto.
    const onStyleData = () => {
      if (!canAccessMapStyle(map) || !map.isStyleLoaded()) return;
      if (hasCadCanvasImageLayer(map)) {
        moveCadCanvasImageBelowParcels(map);
        return;
      }
      apply();
    };
    map.on('styledata', onStyleData);

    return () => {
      cancelled = true;
      loadingUrlRef.current = '';
      clearDeferredTimers();
      try { map.off('error', onError); } catch (_) { /* noop */ }
      try { map.off('styledata', onStyleData); } catch (_) { /* noop */ }
      unbindReady();
    };
  }, [enabled, mapInstanceRef, url]);

  useEffect(() => {
    if (!enabled || !url || !coordinates) return undefined;
    const map = mapInstanceRef.current;
    if (!map) return undefined;
    const sync = () => {
      const prepared = resolvedRef.current.from === url ? resolvedRef.current.prepared : null;
      if (prepared && map.getSource(CAD_CANVAS_IMAGE_SOURCE_ID)) {
        setCadCanvasImageCoordinates(map, coordinates);
        return;
      }
      if (prepared) {
        try {
          ensureCadCanvasImage(map, prepared, coordinates, lastFromRef, lastCanvasRef);
        } catch (error) {
          console.error('CAD: no se pudo pintar la ilustración', error);
        }
        return;
      }
      applyRef.current();
    };
    if (map.isStyleLoaded()) {
      sync();
      return undefined;
    }
    return whenMapStyleReady(map, sync);
  }, [coordsKey, coordinates, enabled, mapInstanceRef, url]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled || !url) return undefined;
    const opacity = guideMode ? CAD_PARCEL_STOCK_STYLE.alignRasterOpacity : 1;
    const apply = () => {
      try {
        if (!map.getLayer(CAD_CANVAS_IMAGE_LAYER_ID)) return;
        map.setPaintProperty(CAD_CANVAS_IMAGE_LAYER_ID, 'raster-opacity-transition', {
          duration: 160,
          delay: 0,
        });
        map.setPaintProperty(CAD_CANVAS_IMAGE_LAYER_ID, 'raster-opacity', opacity);
      } catch (_) { /* estilo a medias */ }
    };
    if (map.isStyleLoaded()) {
      apply();
      return undefined;
    }
    return whenMapStyleReady(map, apply);
  }, [enabled, guideMode, mapInstanceRef, url]);

  useEffect(() => () => {
    lastFromRef.current = null;
    coordsRef.current = null;
    loadingUrlRef.current = '';
    detachCadCanvasImageElement(lastCanvasRef.current);
    lastCanvasRef.current = null;
    resolvedRef.current = { from: '', prepared: null };
    removeCadCanvasImage(mapInstanceRef.current);
  }, [mapInstanceRef]);
}
